import { useEffect, useRef, useState } from "react";
import {
  createExtension,
  deleteExtension,
  getListExtension,
  updateExtension,
} from "../../api/extension";
import { useQuery } from "react-query";
import { isNumeric } from "../../utils";

const useExtensionMaster = () => {
  const [arrExtensions, setArrExtensions] = useState([]);
  const [activeSortColumn, setActiveSortColumn] = useState(0);
  const [sortColumnDirection, setSortColumnDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [newExtension, setNewExtension] = useState({
    name: "",
    cost: "",
    therapist_amount: "",
    extra_time: "",
    remarks: "",
    display: 1,
  });

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [editExtension, setEditExtension] = useState(null);
  const [extensionToDelete, setExtensionToDelete] = useState(null);
  const [isPopupOpenUpdate, setIsPopupOpenUpdate] = useState(false);
  const [isPopupOpenDelete, setIsPopupOpenDelete] = useState(false);
  const [isPopupOpenToggle, setIsPopupOpenToggle] = useState(false);
  const [extensionToToggle, setExtensionToToggle] = useState(null);
  const [nextDisplayValue, setNextDisplayValue] = useState(1);
  const [focusState, setFocusState] = useState({
    name: false,
    cost: false,
  });

  const nameInputRef = useRef(null);

  const [errors, setErrors] = useState({
    name: "",
    cost: "",
    therapist_amount: "",
    extra_time: "",
    remarks: "",
    display: "",
  });

  const [errorsUpdate, setErrorsUpdate] = useState({
    name: "",
    cost: "",
    therapist_amount: "",
    extra_time: "",
    remarks: "",
    display: "",
  });

  const { data, refetch } = useQuery(
    ["extensions", page, rowsPerPage],
    () => {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      return getListExtension(params);
    },
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (data) {
      setArrExtensions(transformExtensionData(data.data.data));
      setTotal(data.data.total);
    }
  }, [data]);

  useEffect(() => {
    setSortedData(arrExtensions.sort((a, b) => b.id - a.id));
  }, [arrExtensions]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const transformExtensionData = (data) => {
    return data.map((extension) => ({
      id: extension.id.toString(),
      name: extension.name,
      cost: Number.parseInt(extension.cost, 10),
      therapist_amount: Number.parseInt(extension.therapist_amount, 10) || 0,
      extra_time: extension.extra_time || 0,
      remarks: extension.remarks || "",
      display: Number(extension.display ?? 1),
    }));
  };

  const handleSort = (columnIndex) => {
    const isSorted = activeSortColumn === columnIndex;
    const direction = isSorted
      ? sortColumnDirection === "asc"
        ? "desc"
        : "asc"
      : "desc";
    const sorted = [...sortedData].sort((a, b) => {
      let aValue = a[Object.keys(a)[columnIndex]];
      let bValue = b[Object.keys(b)[columnIndex]];

      // if aValue and bValue are numberic, sort parseInt
      if (isNumeric(aValue) && isNumeric(bValue)) {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortedData(sorted);
    setActiveSortColumn(columnIndex);
    setSortColumnDirection(direction);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewExtension({ ...newExtension, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddExtension = async () => {
    try {
      const newExtensionData = {
        name: newExtension.name,
        cost: newExtension.cost,
        therapist_amount: newExtension.therapist_amount,
        extra_time: newExtension.extra_time,
        remarks: newExtension.remarks,
        display: newExtension.display,
      };
      const response = await createExtension(newExtensionData);

      if (response && response.data) {
        refetch();
        setNewExtension({
          name: "",
          cost: "",
          therapist_amount: "",
          extra_time: "",
          remarks: "",
          display: 1,
        });
        setErrors({
          name: "",
          cost: "",
          therapist_amount: "",
          extra_time: "",
          remarks: "",
          display: "",
        });
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrors((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
    }
  };

  const handleUpdateExtension = async (updatedExtension) => {
    try {
      const { id, name, cost,therapist_amount,extra_time, remarks, display } = updatedExtension;
      const updatedData = {
        name,
        cost: cost,
        therapist_amount: therapist_amount,
        extra_time: extra_time,
        remarks: remarks,
        display,
      };

      const response = await updateExtension(id, updatedData);

      if (response && response.data) {
        refetch();
        return true;
      } else {
        alert("エラーが発生しました。");
        return false;
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrorsUpdate((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
      return false;
    }
  };

  const handleDeleteExtension = async (id) => {
    try {
      const response = await deleteExtension(id);

      if (response.success) {
        refetch();
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      alert("エラーが発生しました。");
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    const maxPage = Math.floor(total / newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(Math.min(page, maxPage));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFocus = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: true,
    }));
  };

  const handleBlur = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: false,
    }));
  };

  const handleEditClick = (extension) => {
    setEditExtension(extension);
    setIsPopupOpenUpdate(true);
  };

  const handleDeleteClick = (extension) => {
    setExtensionToDelete(extension);
    setIsPopupOpenDelete(true);
  }

  const handleClosePopup = () => {
    setIsPopupOpenUpdate(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setEditExtension((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorsUpdate((prev) => ({
     ...prev,
      [name]: "",
    }));
  };

  const handleToggleDisplay = (_, checked) => {
    setNewExtension((prev) => ({
      ...prev,
      display: checked ? 1 : 0,
    }));
    setErrors((prev) => ({ ...prev, display: "" }));
  };

  const handleToggleDisplayEdit = (_, checked) => {
    setEditExtension((prev) => ({
      ...prev,
      display: checked ? 1 : 0,
    }));
    setErrorsUpdate((prev) => ({ ...prev, display: "" }));
  };

  const handleOpenTogglePopup = (extension, nextValue) => {
    setExtensionToToggle(extension);
    setNextDisplayValue(nextValue);
    setIsPopupOpenToggle(true);
  };

  const handleConfirmToggleDisplay = async () => {
    if (!extensionToToggle) return;
    try {
      const payload = {
        name: extensionToToggle.name,
        cost: extensionToToggle.cost,
        therapist_amount: extensionToToggle.therapist_amount,
        extra_time: extensionToToggle.extra_time,
        remarks: extensionToToggle.remarks,
        display: nextDisplayValue,
      };
      const response = await updateExtension(extensionToToggle.id, payload);

      if (response && response.data) {
        await refetch();
        setIsPopupOpenToggle(false);
        setExtensionToToggle(null);
        return;
      }
      alert("エラーが発生しました。");
    } catch (error) {
      alert("エラーが発生しました。");
    } finally {
      setIsPopupOpenToggle(false);
    }
  };

  return {
    arrExtensions,
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    newExtension,
    rowsPerPage,
    page,
    total,
    editExtension,
    extensionToDelete,
    isPopupOpenUpdate,
    isPopupOpenDelete,
    isPopupOpenToggle,
    focusState,
    nameInputRef,
    errors,
    errorsUpdate,
    setIsPopupOpenDelete,
    setIsPopupOpenToggle,
    handleRowsPerPageChange,
    handlePageChange,
    handleSort,
    handleInputChange,
    handleAddExtension,
    handleUpdateExtension,
    handleDeleteExtension,
    handleFocus,
    handleBlur,
    handleEditClick,
    handleDeleteClick,
    handleClosePopup,
    handleChange,
    handleToggleDisplay,
    handleToggleDisplayEdit,
    handleOpenTogglePopup,
    handleConfirmToggleDisplay,
    extensionToToggle,
    nextDisplayValue,
  };
};

export default useExtensionMaster;
